"use client";

import { useSyncExternalStore, type ReactNode } from "react";
import { SOURCES, type SourceKey } from "@/lib/climate/evidence";
import s from "./climate.module.css";

const subscribe = (callback: () => void) => {
  const query = window.matchMedia("(prefers-reduced-motion: reduce)");
  query.addEventListener("change", callback);
  return () => query.removeEventListener("change", callback);
};
export function useChartAnimation() {
  return useSyncExternalStore(
    subscribe,
    () => !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    () => false,
  );
}
export function Sources({ ids }: { ids: SourceKey[] }) {
  return (
    <div className={s.source}>
      {ids.map((id) => (
        <a key={id} href={SOURCES[id].url} target="_blank" rel="noreferrer">
          {SOURCES[id].label} ↗
        </a>
      ))}
    </div>
  );
}
export function Segments<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className={s.segments} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
export function DataTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: (string | number)[][];
}) {
  return (
    <details className={s.tableDetails}>
      <summary>View chart data</summary>
      <div className={s.tableScroll}>
        <table>
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((value, j) =>
                  j === 0 ? (
                    <th scope="row" key={j}>
                      {value}
                    </th>
                  ) : (
                    <td key={j}>{value}</td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
export function SectionTitle({
  number,
  title,
  children,
  controls,
}: {
  number: string;
  title: string;
  children: ReactNode;
  controls?: ReactNode;
}) {
  return (
    <div className={s.sectionHead}>
      <div>
        <div className={s.eyebrow}>{number}</div>
        <h2>{title}</h2>
        <p>{children}</p>
      </div>
      {controls}
    </div>
  );
}
export function RangeChart({
  rows,
  max,
  unit,
  selected,
  onSelect,
  label,
  pointLabel = "median",
}: {
  rows: { name: string; low: number; high: number; value?: number }[];
  max: number;
  unit: string;
  selected: number;
  onSelect: (index: number) => void;
  label: string;
  pointLabel?: string;
}) {
  return (
    <div role="group" aria-label={label}>
      <div className={s.rangeList}>
        {rows.map((row, index) => (
          <button
            className={s.rangeRow}
            key={row.name}
            type="button"
            aria-pressed={index === selected}
            onClick={() => onSelect(index)}
            aria-label={`${row.name}: ${row.low} to ${row.high} ${unit}${row.value !== undefined ? `, ${pointLabel} ${row.value}` : ""}`}
          >
            <span className={s.rangeName}>{row.name}</span>
            <span className={s.rangeTrack} aria-hidden="true">
              <span
                className={s.rangeBand}
                style={{
                  left: `${(row.low / max) * 100}%`,
                  width: `${((row.high - row.low) / max) * 100}%`,
                }}
              />
              {row.value !== undefined && (
                <span
                  className={s.rangePoint}
                  style={{ left: `${(row.value / max) * 100}%` }}
                />
              )}
            </span>
            <span className={s.rangeValue}>
              {row.low}–{row.high} {unit}
            </span>
          </button>
        ))}
      </div>
      <div className={s.rangeAxis} aria-hidden="true">
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => (
          <span key={fraction} style={{ left: `${fraction * 100}%` }}>
            {Math.round(fraction * max)}
            {fraction === 1 ? ` ${unit}` : ""}
          </span>
        ))}
      </div>
    </div>
  );
}
